import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, UserAvatar, ChangeTrack, Tooltip, RequesterEmails, ClientReplyPendingBadge } from '../../../components';
import { Board, Change, ProjectLabel } from '../../../models';
import type { ColumnData } from '../../../models';
import { useAppSelector } from '../../../redux';
import { Table, Badge, Progress } from 'flowbite-react';
import moment from 'moment';
import { formatMomentDate } from '../../../locales';
import { clientPath } from '../../../utils/clientPath';
import {
    BOARD_LIST_CONFIGURABLE_COLUMNS,
    BoardListColumnId,
    loadBoardListViewPrefs,
    moveBoardListColumn,
    saveBoardListViewPrefs,
    toggleBoardListColumn,
} from '../../../utils/boardListViewPrefs';

interface ListProps {
    board: Board;
    onEditCard: (change: Change) => void;
    onLabelClick?: (labelId: string) => void;
    hideLabelNames?: boolean;
    compact?: boolean;
    sort?: 'newest' | 'oldest' | 'priority';
    showPinAction?: boolean;
    pinnedChangeIds?: string[];
    onTogglePin?: (change: Change) => void;
    onOpenInNewTab?: (change: Change) => void;
}

const List = (props: ListProps) => {
    const { t, i18n }           = useTranslation();
    const {
        board, onEditCard, onLabelClick, hideLabelNames, compact, sort = 'newest',
        showPinAction, pinnedChangeIds, onTogglePin, onOpenInNewTab,
    } = props;

    const { user }   = useAppSelector(state => state.auth);
    const { active } = useAppSelector(state => state.tracks);
    const { project } = useAppSelector(state => state.projects);

    const prefsScope = useMemo(() => ({
        userId: user?._id,
        projectId: (typeof board?.project === 'string' ? board.project : board?.project?._id)
            || project?._id,
        boardId: board?._id,
    }), [user?._id, board?.project, board?._id, project?._id]);

    const [columns, setColumns] = useState<BoardListColumnId[]>(
        () => loadBoardListViewPrefs(prefsScope).columns
    );
    const [columnsOpen, setColumnsOpen] = useState(false);

    useEffect(() => {
        setColumns(loadBoardListViewPrefs(prefsScope).columns);
        setColumnsOpen(false);
    }, [prefsScope.userId, prefsScope.projectId, prefsScope.boardId]);

    useEffect(() => {
        saveBoardListViewPrefs(prefsScope, { columns });
    }, [columns, prefsScope.userId, prefsScope.projectId, prefsScope.boardId]);

    let projectsMap: Record<any, boolean> = {};
    board.columns.forEach((column: ColumnData) => column.changes.forEach((change: Change) => {
        projectsMap[change.project._id] = true;
    }));
    const projectsIds = Object.keys(projectsMap);

    const visibleColumns = useMemo(() => {
        return columns.filter((id) => id !== 'project' || projectsIds.length > 1);
    }, [columns, projectsIds.length]);

    /** Picker order mirrors table order; unchecked columns stay at the end. */
    const pickerColumns = useMemo(() => {
        const configurable = BOARD_LIST_CONFIGURABLE_COLUMNS.filter(
            (columnId) => columnId !== 'project' || projectsIds.length > 1
        );
        const visibleOrdered = columns.filter((id) => configurable.includes(id));
        const hidden = configurable.filter((id) => !columns.includes(id));
        return [...visibleOrdered, ...hidden];
    }, [columns, projectsIds.length]);

    const columnLabel = (columnId: BoardListColumnId): string => {
        switch (columnId) {
            case 'change': return t('changes.change') || '';
            case 'project': return t('projects.project') || '';
            case 'column': return t('changes.column') || '';
            case 'assignee': return t('changes.assignee') || '';
            case 'status': return t('changes.status') || '';
            case 'estimate': return t('changes.estimate') || '';
            case 'deadline': return t('changes.deadline') || '';
            case 'importance': return t('changes.importance') || '';
            case 'priority': return t('changes.priority') || '';
            case 'client': return t('changes.meta_client') || '';
            case 'requester': return t('priorities.list.columns.requester') || '';
            case 'updatedAt': return t('priorities.list.columns.updatedAt') || '';
            default: return columnId;
        }
    };

    const clientIdOf = (change: Change): string | undefined => {
        const client = change.client;
        if (!client)
            return undefined;
        if (typeof client === 'string')
            return client;
        return client._id || undefined;
    };

    const clientLabel = (change: Change): string => {
        const client = change.client;
        if (!client)
            return '';
        if (typeof client === 'string')
            return client;
        if (typeof client.name === 'function')
            return client.name();
        return [client.id, client.company, client.firstname, client.lastname]
            .filter(Boolean)
            .join(' - ');
    };

    const renderChangeCell = (change: Change) => (
        <div className={`flex items-start space-x-2 ${compact ? 'space-x-1' : ''}`}>
            <div className={`flex-col min-w-0 ${compact ? 'space-y-0.5' : 'space-y-1'}`}>
                <h4 className={`font-medium text-gray-900 dark:text-gray-100 ${compact ? 'text-sm' : 'heading-md'}`}>
                    {change.title.fr || ""}
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                    <Badge color={change.color()} className="w-fit text-xs px-1">
                        <Icon type={change.iconName()} size={3} className="inline" />&nbsp;
                        {change.slugName()}
                    </Badge>
                    {(change.nbComments > 0) && (
                        <Badge color="indigo" className="w-fit text-xs px-1">
                            <Icon type="comment" className="inline" />&nbsp;
                            {change.nbComments}
                        </Badge>
                    )}
                    {change.nbAttachments > 0 && (
                        <Badge color="gray" className="w-fit text-xs px-1">
                            <Icon type="file" className="inline" />&nbsp;
                            {change.nbAttachments}
                        </Badge>
                    )}
                    {change.hasTechnicalContext() && (
                        <Badge color="indigo" className="w-fit text-xs px-1">
                            <Icon type="notes" className="inline" />
                        </Badge>
                    )}
                    <ClientReplyPendingBadge change={change} dismissable />
                    {change.quote && (
                        <Badge color="blue" className="w-fit text-xs px-1">
                            <Icon type="law" className="inline" />&nbsp;
                            {change.quote.slug} | {t(`quotes.status_type.${change.quote.status}`)}
                        </Badge>
                    )}
                    {change.release && (
                        <Badge color="indigo" size="sm" className="w-fit inline-flex items-center gap-1.5 px-1.5">
                            <Icon type="release" color="indigo" className="inline shrink-0"/>
                            {change.release.version}
                        </Badge>
                    )}
                    {(change.commits?.length > 0) && (
                        <div className="flex items-center space-x-1">
                            {change.commits?.map(commit => (
                            <Badge key={commit.hash} color="success" className="w-fit px-1">
                                <Icon type="commit" size={3} className="inline"/>&nbsp;
                                {commit.message}
                            </Badge>
                            ))}
                        </div>
                    )}
                    {((project?.labels || []).length > 0 && (change.labelIds || []).length > 0) && (
                        <div className={`ml-auto flex flex-wrap items-center ${hideLabelNames ? 'gap-0.5' : 'gap-1'}`}>
                            {(project.labels || [])
                                .filter((label: ProjectLabel) => (change.labelIds || []).includes(label._id))
                                .map((label: ProjectLabel) => (
                                        <button
                                            key={label._id}
                                            type="button"
                                            className={hideLabelNames
                                                ? 'h-2 w-10 shrink-0 rounded-sm'
                                                : 'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium'}
                                            style={{
                                                backgroundColor: label.color,
                                                color: hideLabelNames ? undefined : ProjectLabel.textColor(label.color),
                                            }}
                                            title={label.name}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onLabelClick?.(label._id);
                                            }}
                                        >
                                            {!hideLabelNames && (
                                                <>
                                                    <Icon type="tag" size={3} className="shrink-0" color="inherit" />
                                                    <span>{label.name}</span>
                                                </>
                                            )}
                                        </button>
                                    ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderCell = (change: Change, column: ColumnData, columnId: BoardListColumnId) => {
        const progression = Math.round(change.progression());
        const cellClass = compact ? '!py-1.5' : undefined;

        switch (columnId) {
            case 'change':
                return <Table.Cell key={columnId} className={cellClass}>{renderChangeCell(change)}</Table.Cell>;
            case 'project':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        <span className="font-medium">{change.project?.name}</span>
                    </Table.Cell>
                );
            case 'column':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        <span className="font-medium">{column.title}</span>
                    </Table.Cell>
                );
            case 'assignee':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        {change.assignee && !change.isTimeTrackable(user) && (
                            <UserAvatar size={6} user={change.assignee} noTooltip className="my-1" />
                        )}
                        {change.assignee && change.isTimeTrackable(user) && (
                            <div className="group">
                                <div className="h-6 w-6 flex justify-center items-center">
                                    {active?.change?._id === change._id ? (
                                        <ChangeTrack className="group-hover:hidden" change={change} />
                                    ) : (
                                        <UserAvatar size={6} user={change.assignee} noTooltip className="group-hover:hidden my-1"/>
                                    )}
                                    <ChangeTrack className="hidden group-hover:block" change={change} />
                                </div>
                            </div>
                        )}
                        {!change.assignee && (
                            <div className="h-6 w-6"></div>
                        )}
                    </Table.Cell>
                );
            case 'status':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        <div className="flex-col space-y-1">
                            <div className="text-xs font-light border border-gray-100 dark:border-gray-700 rounded px-2 whitespace-nowrap flex flex-row justify-between">
                                <span className={`whitespace-nowrap text-ellipsis overflow-hidden truncate inline-block ${change.estimate > 0 ? 'max-w-[9rem]' : ''}`}>
                                    <Icon type="progress" size={3} className="inline mr-1"/>&nbsp;
                                    {t(change.progressStatusLabelKey())}
                                </span>
                            </div>
                            <Tooltip content={progression + '%'} placement="top">
                                <Progress
                                    progress={progression}
                                    size="sm"
                                    color={ progression === 100 ? 'green' : (progression > 20 ? 'cyan' : 'yellow')}
                                />
                            </Tooltip>
                        </div>
                    </Table.Cell>
                );
            case 'estimate':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        { change.estimate > 0 && (
                            <Badge color="warning" className="w-fit whitespace-nowrap px-1">
                                <>
                                    <Icon type="estimated" size={3} className="inline mr-1"/>&nbsp;
                                    {change?.estimate} {t('changes.estimate_units_min')}
                                </>
                            </Badge>
                        )}
                    </Table.Cell>
                );
            case 'deadline':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        <div className="flex-col space-y-1">
                            {change.deadline && (
                                <Badge color={change.deadline.isBefore(moment().startOf('day')) ? 'pink' : (change.deadline.isAfter(moment()) ? 'green' : 'indigo')} size="xs" className="w-fit">
                                    <span className="font-normal">
                                        {formatMomentDate(change.deadline, 'L')}
                                    </span>
                                </Badge>
                            )}
                            {(change.sprints?.length > 0) && (
                                <Badge color="warning" size="xs" className="w-fit px-1">
                                    <span className="text-xs font-normal">
                                        <Icon type="sprint" color="indigo" className="inline mr-1.5"/>
                                        {change.sprints.slice(-1)[0].name}
                                    </span>
                                </Badge>
                            )}
                        </div>
                    </Table.Cell>
                );
            case 'importance':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        {t(`changes.importance_value_${change.importance}`)}
                    </Table.Cell>
                );
            case 'priority':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        <Badge color="indigo" className="w-fit" size="xs">
                            <Icon type="priority" className="inline mr-1" />
                            {change.priority}
                        </Badge>
                    </Table.Cell>
                );
            case 'client': {
                const clientId = clientIdOf(change);
                const label = clientLabel(change);
                return (
                    <Table.Cell
                        key={columnId}
                        className={cellClass}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {clientId && label ? (
                            <Link
                                to={clientPath(clientId)}
                                className="truncate text-indigo-600 dark:text-indigo-300 hover:underline text-xs"
                                title={t('client_view.open_fiche') || ''}
                            >
                                {label}
                            </Link>
                        ) : (
                            <span className="truncate text-gray-700 dark:text-gray-200 text-xs">
                                {label}
                            </span>
                        )}
                    </Table.Cell>
                );
            }
            case 'requester':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        <RequesterEmails source={change} className="truncate text-gray-700 dark:text-gray-200 text-xs" />
                    </Table.Cell>
                );
            case 'updatedAt':
                return (
                    <Table.Cell key={columnId} className={cellClass}>
                        <span className="whitespace-nowrap text-gray-600 dark:text-gray-300 text-xs">
                            {change.updatedAt
                                ? formatMomentDate(change.updatedAt, 'dd DD MMM YYYY HH:mm')
                                : '—'}
                        </span>
                    </Table.Cell>
                );
            default:
                return null;
        }
    };

    const sortedRows = React.useMemo(() => {
        const rows: { change: Change; column: ColumnData }[] = [];
        (board.columns || []).forEach((column: ColumnData) => {
            (column.changes || []).forEach((change: Change) => {
                rows.push({ change, column });
            });
        });

        rows.sort((a, b) => {
            if (sort === 'priority') {
                return (b.change.priority || 0) - (a.change.priority || 0);
            }
            const aTime = a.change.updatedAt ? new Date(a.change.updatedAt).getTime() : 0;
            const bTime = b.change.updatedAt ? new Date(b.change.updatedAt).getTime() : 0;
            return sort === 'oldest' ? aTime - bTime : bTime - aTime;
        });

        return rows;
    }, [board, sort]);

    return (
        <main className="overflow-x-auto no-scrollbar h-full w-full">
            <div className="relative z-20 flex items-center justify-end gap-2 mb-1.5">
                <div className="relative">
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => setColumnsOpen((open) => !open)}
                    >
                        <Icon type="setting" size={3} />
                        {t('priorities.list.columns_menu')}
                    </button>
                    {columnsOpen && (
                        <div className="absolute right-0 top-full z-40 mt-1 w-64 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 shadow-lg">
                            <div className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
                                {t('priorities.list.columns_menu')}
                            </div>
                            <ul className="max-h-72 space-y-1 overflow-auto">
                                {pickerColumns.map((columnId) => {
                                        const visible = columns.includes(columnId);
                                        const visibleIndex = visibleColumns.indexOf(columnId);
                                        const canMoveUp = visible && visibleIndex > 1; // index 0 is always "change"
                                        const canMoveDown = visible
                                            && visibleIndex >= 0
                                            && visibleIndex < visibleColumns.length - 1;
                                        return (
                                            <li key={columnId} className="flex items-center gap-1 text-xs">
                                                <input
                                                    type="checkbox"
                                                    className="h-3.5 w-3.5"
                                                    checked={visible}
                                                    onChange={(e) => setColumns(
                                                        toggleBoardListColumn(columns, columnId, e.target.checked)
                                                    )}
                                                />
                                                <span className="flex-1 truncate">
                                                    {columnLabel(columnId)}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="rounded px-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 disabled:opacity-30"
                                                    disabled={!canMoveUp}
                                                    onClick={() => setColumns(
                                                        moveBoardListColumn(columns, columnId, 'up')
                                                    )}
                                                    title={t('priorities.list.move_up') as string}
                                                >
                                                    <Icon type="up" size={3} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="rounded px-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 disabled:opacity-30"
                                                    disabled={!canMoveDown}
                                                    onClick={() => setColumns(
                                                        moveBoardListColumn(columns, columnId, 'down')
                                                    )}
                                                    title={t('priorities.list.move_down') as string}
                                                >
                                                    <Icon type="down" size={3} />
                                                </button>
                                            </li>
                                        );
                                    })}
                            </ul>
                            <p className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2 text-[10px] text-gray-500 dark:text-gray-400">
                                {t('projects.list_change_always_visible')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
            <Table striped hoverable className="w-[calc(100vw - 2rem)] border rounded">
                <Table.Head>
                    {showPinAction && onTogglePin && (
                        <Table.HeadCell className="w-10">
                            <Icon type="pin" size={3} color="gray-400" />
                        </Table.HeadCell>
                    )}
                    {visibleColumns.map((columnId) => (
                        <Table.HeadCell key={columnId}>
                            {columnLabel(columnId)}
                        </Table.HeadCell>
                    ))}
                </Table.Head>
                <Table.Body>
                    {sortedRows.map(({ change, column }) => {
                        const isPinned = !!change._id && (pinnedChangeIds || []).includes(change._id);
                        return (
                        <Table.Row
                            key={change._id}
                            className={`cursor-pointer border-t ${compact ? 'text-xs' : ''}`}
                            onClick={() => onEditCard(change)}
                            onMouseDown={(event) => {
                                if (event.button === 1)
                                    event.preventDefault();
                            }}
                            onAuxClick={(event) => {
                                if (event.button !== 1 || !onOpenInNewTab)
                                    return;
                                event.preventDefault();
                                onOpenInNewTab(change);
                            }}
                        >
                            {showPinAction && onTogglePin && (
                                <Table.Cell className="w-10" onClick={(event) => event.stopPropagation()}>
                                    <Tooltip content={isPinned ? (t('changes.unpin') || '') : (t('changes.pin') || '')}>
                                        <button
                                            type="button"
                                            className={`inline-flex h-6 w-6 items-center justify-center rounded ${
                                                isPinned
                                                    ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                                    : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-amber-700'
                                            }`}
                                            aria-label={isPinned ? (t('changes.unpin') || '') : (t('changes.pin') || '')}
                                            aria-pressed={isPinned}
                                            onClick={() => onTogglePin(change)}
                                        >
                                            <Icon type="pin" size={3} color={isPinned ? 'amber-800' : 'gray-400'} />
                                        </button>
                                    </Tooltip>
                                </Table.Cell>
                            )}
                            {visibleColumns.map((columnId) => renderCell(change, column, columnId))}
                        </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
        </main>
    );
};

export default List;
